<template>
  <div>
    <label class="label flex flex-align-center flex-justify-between text-smaller">
      <span class="text-uppercase">
        Line {{ index + 1 }} &ndash; Starting&nbsp;#
      </span>
      <span class="text-normal text-muted text-nowrap text-tiny">
        {{ lineLength }}/{{ line.maxlength }}
      </span>
    </label>

    <div class="form-column" style="margin-top: .25em;">
      <div class="flex flex-wrap-nowrap flex-align-center">
        <input
          ref="input"
          type="text"
          class="input-field"
          :class="{'parsley-error': errors.value}"
          :maxlength="line.maxlength"
          placeholder="XX0001"
          :value="line.value || ''"
          :readonly="disabled"
          @keypress="preventCharacters"
          @input="updateValue($event.target.value, $event)"
          @blur="formatValue($event.target.value)"
        />
        <svg v-if="disabled" class="block text-muted" style="margin-left: -24px;" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
          <use href="#input-lock-icon"></use>
        </svg>
      </div>
      <div
        class="error-text"
        style="margin-top: .25em;"
        v-if="errors.value">
        {{ errors.value }}
      </div>
      <div
        v-else-if="line.value"
        class="text-tiny text-muted"
        style="margin-top: .25em;"
      >
        Range: {{ line.value }}&nbsp;&ndash;&nbsp;{{ endSequence }}
      </div>
      <div
        v-if="conflicts.length > 0"
        class="error error-text"
        style="margin-top: .25em;"
      >
        <div class="text-bold text-smaller text-uppercase">
          Sequence Error
        </div>
        <div
          v-for="c in conflicts"
          :key="`${c.startAin}`"
          class="flex flex-justify-between"
          style="margin-top: .25em;"
        >
          <span>{{ c.startAin }} &ndash; {{ c.endAin }}</span>
          <span>{{ formatDate(c.eventDate) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { dispatchInputEvent, formatDate, randomString } from '../helpers.js';

const sequencePattern = /^([A-Za-z]*)?(\d+)$/;

export default {
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    eid: {
      type: Boolean,
      default: false,
    },
    index: {
      default: 0,
      type: Number,
    },
    line: {
      required: true,
      type: Object,
    },
    quantity: {
      default: 0,
      type: Number,
    },
    scrapie: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      conflicts: [],
      errors: {},
      id: '',
    };
  },

  created() {
    this.id = randomString(24);
  },

  mounted() {
    this.$nextTick(() => {
      const s = (this.$root.$refs['eartag-app'].filledLines || {}).sequence || false;

      if(this.$refs.input) {
        if(this.scrapie && s) {
          this.$refs.input.value = s;
        }

        this.triggerChange();
      }
    });
  },

  methods: {
    checkSequence(value) {
      value = (value !== undefined && value !== null) ? value : this.line.value || '';

      this.$emit('update:sequence', value);

      if(!this.scrapie || this.eid){
        return;
      }

      let matches = (value).match(sequencePattern);

      if(!matches) return false;

      const prefix      = matches[1];
      const regex       = new RegExp('^' + (prefix || '') + '\\d+');
      const prefixRegex = new RegExp('^' + (prefix || ''));
      const start       = Number(matches[2]);
      const end         = start + this.quantity;

      let overlap = this.$parent.sequences
                      .filter(s => regex.test(s.startAin))
                      .filter(s => {
                        const rangeStart = Number(s.startAin.replace(prefixRegex, ''));
                        const rangeEnd   = Number(s.endAin.replace(prefixRegex, ''));

                        if(isNaN(rangeStart)) {
                          return false;
                        }

                        if(rangeStart <= end && rangeEnd >= start) {
                          return true;
                        }

                        return false;
                      });

      if(overlap.length > 0) {
        this.conflicts = overlap;
        this.$set(this.errors, 'value', "Sequence has already been used.");
      } else {
        this.conflicts = [];
        this.$delete(this.errors, 'value');
      }
    },

    formatDate,

    formatValue(value) {
      let matches = (value || '').match(sequencePattern),
          end,
          lineValue,
          prefix,
          start,
          pad;

      if(!matches) return;

      prefix = String(matches[1] || '');
      start = Number(matches[2]);
      end = start + this.quantity - 1;
      pad = this.scrapie
        ? (this.eid ? Math.max(String(end).length, String(start).length) : 4) - prefix.length
        : 2;

      lineValue = prefix
        + String(Number(matches[2]))
          .padStart(pad, '0');

      this.updateValue(lineValue);
    },

    preventCharacters(e) {
      if(this.scrapie && ['i', 'o', 'q'].indexOf(e.key.toLowerCase()) !== -1) {
        e.preventDefault();
      }
    },

    triggerChange() {
      dispatchInputEvent(this.$refs.input, 'input');
    },

    updateValue(value, e) {
      this.$delete(this.errors, 'value');

      value = String(value.replace(/[^A-Za-z0-9]+/i, ''))
              .toUpperCase();

      // format sequence and validate
      if(value !== '' && sequencePattern.test(value) === false) {
        this.$set(this.errors, 'value', 'Invalid sequence value.');
      } else if(value.length > this.line.maxlength) {
        this.$set(this.errors, 'value', 'Sequence is too long.');
      } else {
        if(!this.scrapie && (this.errors.value || false)) {
          this.$delete(this.errors, 'value');
        }
      }

      if(this.line.value !== value) {
        this.$emit('update:line', Object.assign(
          {},
          this.line,
          {value: value}
        ));
      }

      this.checkSequence(value);
    },
  },

  computed: {
    endSequence() {
      let matches = (this.line.value || '').match(sequencePattern),
        pad,
        end,
        prefix;

      if(!matches) return '';

      prefix = String(matches[1] || '');
      end = String(Number(matches[2]) + this.quantity - 1);
      pad = this.scrapie ? (this.eid ? String(end).length : 4) - prefix.length : 2;

      return prefix + end.padStart(pad, '0');
    },

    lineLength() {
      return this.line.maxlength - (this.line.value || '').length;
    },
  },

  watch: {
    errors: {
      deep: true,
      handler(errors) {
        this.$emit('child-error', Object.keys(errors).length > 0);
      },
    },

    'line.value': {
      immediate: true,
      handler(value){
        this.updateValue(value || '');
      },
    },

    quantity() {
      this.formatValue(this.line.value || '');
    },
  },
}
</script>
